import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef } from 'react';
import { cn } from '../../lib/cn.ts';

const cardVariants = cva('border border-line rounded-lg', {
  variants: {
    padding: {
      md: 'p-5.5',
      /*
       * For a card that holds an editable document. The wider left inset is the
       * gutter the block handle lives in — hovering a line puts the plus and the
       * grip there, beside the text rather than out on the page. Everything in
       * the card moves with it, so the title still lines up with the document.
       */
      document: 'p-5.5 pl-14',
      none: 'p-0',
    },
    /*
     * White is the page's content. The other two are cards *about* the content:
     * `quiet` for something carried in from elsewhere and read rather than
     * worked on (the recap of the previous conversation), `accent` for Wundo
     * asking something of the user. Tone is never decoration — a white card
     * that turns green has changed what it is claiming.
     */
    tone: {
      surface: 'bg-surface',
      quiet: 'bg-fill-faint',
      accent: 'border-accent-line bg-accent-tint-faint',
    },
    interactive: {
      true: 'transition hover:shadow-hover hover:-translate-y-px',
      false: '',
    },
  },
  defaultVariants: {
    padding: 'md',
    tone: 'surface',
    interactive: false,
  },
});

type CardElement = 'div' | 'section' | 'article' | 'li';

type CardProps<T extends CardElement = 'div'> = Omit<ComponentPropsWithRef<T>, 'style'> &
  VariantProps<typeof cardVariants> & {
    as?: T;
  };

function Card<T extends CardElement = 'div'>({
  as,
  className,
  padding,
  tone,
  interactive,
  ...props
}: CardProps<T>) {
  /*
   * TS cannot type-check JSX against a union of tags (refs are invariant),
   * so the render site is cast to 'div'; CardProps<T> checks the call site.
   */
  const Component = (as ?? 'div') as 'div';
  return (
    <Component
      data-slot='card'
      className={cn(cardVariants({ padding, tone, interactive }), className)}
      {...(props as Omit<ComponentPropsWithRef<'div'>, 'style'>)}
    />
  );
}

type CardTitleElement = 'h2' | 'h3';

type CardTitleProps<T extends CardTitleElement = 'h2'> = Omit<
  ComponentPropsWithRef<T>,
  'style'
> & {
  as?: T;
};

/*
 * A card's own name: small, uppercase, widely tracked, and quiet enough that
 * the content is what the eye lands on. Grey caps, always, and never an icon
 * beside it — the one mark it carries is the green dot, which says the card is
 * a surface Wundo works on rather than a passive box.
 *
 * A heading element, unlike SectionLabel's default: a card is a landmark in the
 * page and its title is how a screen-reader user finds it.
 */
function CardTitle<T extends CardTitleElement = 'h2'>({
  as,
  className,
  children,
  ...props
}: CardTitleProps<T>) {
  /* Same invariant-ref cast as Card above; CardTitleProps<T> checks the call site. */
  const Component = (as ?? 'h2') as 'h2';
  return (
    <Component
      data-slot='card-title'
      className={cn(
        'flex items-center gap-2 text-xs font-bold tracking-widest text-ink-muted uppercase',
        className,
      )}
      {...(props as Omit<ComponentPropsWithRef<'h2'>, 'style'>)}
    >
      <span
        data-slot='card-title-dot'
        aria-hidden='true'
        className='size-1.5 shrink-0 rounded-full bg-accent'
      />
      {children}
    </Component>
  );
}

export { Card, CardTitle, cardVariants };
export type { CardElement, CardProps, CardTitleElement, CardTitleProps };
export type CardVariants = VariantProps<typeof cardVariants>;
